import { lazy, Suspense, useRef } from 'react';
import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useAtomValue } from 'jotai';

import { HeaderPlaceholder, Main, Wrapper } from './content.styles';

import ErrorBoundary from '../../error_boundary';
import LoadingText from '../../loading_text';
import ScrollToTop from '../../scroll_to_top';
import Title from '../../title';
import Header from './header';
import AcceptTermsAndConditionsPage from './home/card_subscription/accept_terms_and_conditions';
import AppConsentsPage from './home/card_subscription/app_consents';
import OffersPage from './home/card_subscription/offers';
import PlacesPage from './home/card_subscription/places';
import RewardReceivedPage from './home/card_subscription/reward_received';
import RewardsPage from './home/card_subscription/rewards';
import SelectFavoritePlacePage from './home/card_subscription/select_favorite_place';
import TermsAndConditionsPage from './home/card_subscription/terms_and_conditions';

import { vendorCardState } from '../../../store';

const HomePage = lazy(() => import('./home'));
const MapPage = lazy(() => import('./map'));
const FavoritesPage = lazy(() => import('./favorites'));
const FlavorsPage = lazy(() => import('./flavors'));
const InboxPage = lazy(() => import('./inbox'));
const ChallengesPage = lazy(() => import('./challenges'));
const ProfilePage = lazy(() => import('./profile'));
const CameraPage = lazy(() => import('./camera'));
const RedeemPage = lazy(() => import('./redeem'));

const PWAPopover = lazy(() => import('./pwa_popover'));

export default function Content() {
  const location = useLocation();
  const wrapper = useRef<HTMLDivElement>(null);

  const card = useAtomValue(vendorCardState);

  const hasTermsAndConditions = Boolean(card.terms_and_conditions);

  const isMain = location.pathname === '/';

  return (
    <>
      <ErrorBoundary fallback={null}>
        <Suspense fallback={null}>
          <PWAPopover />
        </Suspense>
      </ErrorBoundary>
      <Wrapper isMain={isMain} ref={wrapper}>
        <Title />
        <ScrollToTop wrapper={wrapper} />
        <Routes>
          <Route path="" element={<Header />} />
          <Route path="favorites/*" element={<HeaderPlaceholder />} />
          <Route path="flavors" element={<HeaderPlaceholder />} />
          <Route path="flavors/available" element={<HeaderPlaceholder />} />
          <Route path="flavors/favorited" element={<HeaderPlaceholder />} />
          <Route path="challenges" element={<HeaderPlaceholder />} />
          <Route path="inbox" element={<HeaderPlaceholder />} />
        </Routes>
        <Main>
          <Suspense fallback={<LoadingText />}>
            <Routes>
              <Route path="" element={<HomePage />} />
              <Route path="map/*" element={<MapPage />} />
              <Route path="favorites/*" element={<FavoritesPage />} />
              <Route path="flavors/*" element={<FlavorsPage />} />
              <Route path="inbox/*" element={<InboxPage />} />
              <Route path="challenges/*" element={<ChallengesPage />} />
              <Route path="profile/*" element={<ProfilePage />} />
              <Route path="camera/*" element={<CameraPage />} />
              <Route path="redeem/*" element={<RedeemPage />} />
              <Route path="offers/*" element={<OffersPage />} />
              <Route path="places/*" element={<PlacesPage />} />
              <Route path="rewards/*" element={<RewardsPage />} />
              <Route path="reward_received/*" element={<RewardReceivedPage />} />
              <Route path="app_consents/*" element={<AppConsentsPage />} />
              {hasTermsAndConditions ? (
                <Route
                  path="accept_terms_and_conditions/*"
                  element={<AcceptTermsAndConditionsPage />}
                />
              ) : null}
              {hasTermsAndConditions ? (
                <Route path="terms_and_conditions/*" element={<TermsAndConditionsPage />} />
              ) : null}
              <Route path="select_favorite_place/*" element={<SelectFavoritePlacePage />} />
              <Route path="*" element={<Navigate to="/" />} />
            </Routes>
          </Suspense>
        </Main>
      </Wrapper>
    </>
  );
}
